import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@r/components/ui/select.tsx';
import {
  getAssignableProductCategories,
  useAdminShopCategoriesCollection
} from './custom-shop-categories-api.ts';

const NO_CATEGORY_VALUE = '__none__';

interface CustomShopProductCategorySelectProps {
  shopId: number;
  value: number | null;
  onChange: (categoryId: number | null) => void;
  disabled?: boolean;
}

/** Select des catégories disponibles dans la boutique, chargées via l'endpoint admin paginé. */
export function CustomShopProductCategorySelect({
  shopId,
  value,
  onChange,
  disabled = false
}: CustomShopProductCategorySelectProps) {
  const { t } = useTranslation();
  const categoriesQuery = useAdminShopCategoriesCollection(shopId);
  useOrvalQueryErrorToast(categoriesQuery, t('admin.shops.categories.error.load'));

  const categories = useMemo(
    () =>
      getAssignableProductCategories(categoriesQuery.data ?? []).sort((left, right) =>
        left.label.localeCompare(right.label)
      ),
    [categoriesQuery.data]
  );

  return (
    <Select
      disabled={disabled || categoriesQuery.isLoading}
      value={value != null ? String(value) : NO_CATEGORY_VALUE}
      onValueChange={(nextValue) => {
        onChange(nextValue === NO_CATEGORY_VALUE ? null : Number(nextValue));
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={t('admin.shops.products.form.category.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_CATEGORY_VALUE}>
          {t('admin.shops.products.form.category.none')}
        </SelectItem>
        {categories.map((category) => (
          <SelectItem key={category.id} value={String(category.id)}>
            {category.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
